import { Canvas, Text, Control } from 'fabric'
import type { TextAlignment, FontFamily, MarkingPosition } from '../../types'
import {
    createTextObject,
    setupTextObjectControls,
    setupTextObjectEventHandlers,
    applyTextBorderStyles,
    getNormalizedFontFamily,
    waitForFontLoad,
} from '../../utils/text/textObject'
import { createDefaultResizeControl } from '../../utils/text/resizeControl'
import { calculateTextPosition } from '../../utils/text/textPosition'
import { clampFontSize } from '../../utils/validation'
import { convertHexWithAlphaToRgba } from '../../utils/color/colorConversion'
import {
    TEXT_OBJECT_PADDING,
    TEXT_OBJECT_LINE_HEIGHT,
    TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
} from '../../constants'
import type { CreateTextObjectParams } from '../types'

/**
 * Параметры для создания текстового объекта на canvas
 */
export interface CreateTextOnCanvasParams extends CreateTextObjectParams {
    fabricCanvas: Canvas
    onFontSizeChange?: (size: number) => void
    onPositionChange?: (position: MarkingPosition) => void
    isScalingFromSlider: () => boolean
}

/**
 * Создание текстового объекта на canvas
 * @param params - параметры для создания текстового объекта
 * @returns Promise с созданным текстовым объектом
 */
export async function createTextOnCanvas(
    params: CreateTextOnCanvasParams
): Promise<Text> {
    const {
        text,
        fontSize,
        textColor,
        textAlignment,
        fontFamily,
        position,
        hasBackground,
        backgroundColor,
        canvasWidth,
        canvasHeight,
        safeArea,
        fabricCanvas,
        onFontSizeChange,
        onPositionChange,
        isScalingFromSlider,
    } = params

    // Рассчитываем максимальную ширину текста с учетом padding
    // В Fabric.js padding добавляется вокруг текста, увеличивая общую ширину объекта
    // width в Text - это ширина текста БЕЗ padding, общая ширина = width + padding * 2
    // Без ограничений safeArea - пользователь может размещать текст где угодно
    // Вычитаем padding для border (добавляется с обеих сторон)
    const borderPadding = TEXT_OBJECT_PADDING.BORDER * 2
    // Вычитаем padding для фона, если он есть (добавляется с обеих сторон)
    const backgroundPadding =
        hasBackground && backgroundColor !== null
            ? TEXT_OBJECT_PADDING.BACKGROUND * 2
            : 0
    // Максимальная ширина текста = ширина canvas - все padding (без ограничений safeArea)
    const innerTextWidth = Math.max(
        TEXT_OBJECT_PADDING.MIN_WIDTH,
        canvasWidth - borderPadding - backgroundPadding
    )

    // Конвертируем цвет в rgba
    const rgbaColor = convertHexWithAlphaToRgba(textColor)

    // Рассчитываем позицию текста
    const positionToUse: MarkingPosition = position ?? {
        x: 50,
        y: 90, // Снизу по умолчанию
    }

    // Ждем загрузки шрифта перед расчетом размеров
    // Это важно для Inter/Montserrat и других шрифтов, которые загружаются асинхронно
    await waitForFontLoad(fontFamily)

    // Создаем временный текстовый объект для получения размеров
    const tempTextObj = createTextObject({
        text,
        fontSize: clampFontSize(fontSize),
        fill: rgbaColor,
        textAlign: textAlignment,
        fontFamily,
        left: 0,
        top: 0,
        width: innerTextWidth,
    })

    // Пересчитываем размеры после загрузки шрифта
    tempTextObj.initDimensions()

    // Для проблемных шрифтов (Inter, Montserrat) дополнительно пересчитываем размеры через requestAnimationFrame
    // Это гарантирует, что размеры будут пересчитаны после полной загрузки шрифта
    const isKerningSensitiveFont =
        fontFamily === 'Inter' || fontFamily === 'Montserrat'

    if (isKerningSensitiveFont) {
        // Используем Promise для ожидания пересчета размеров
        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
                // Обновляем ширину перед пересчетом
                tempTextObj.set({ width: innerTextWidth })
                tempTextObj.initDimensions()
                resolve()
            })
        })
    }

    // Получаем размеры текста
    const textWidth = tempTextObj.width * (tempTextObj.scaleX ?? 1)
    const textHeight = tempTextObj.height * (tempTextObj.scaleY ?? 1)

    // Рассчитываем позицию текста с учетом выравнивания
    const textPosition = calculateTextPosition({
        position: positionToUse,
        canvasWidth,
        canvasHeight,
        textAlign: textAlignment,
        textWidth,
        textHeight,
        safeArea,
    })

    // Для Inter/Montserrat не применяем подложку при создании, так как размеры будут пересчитаны
    // Для других шрифтов применяем подложку сразу
    const shouldApplyBackgroundNow = !isKerningSensitiveFont

    // Создаем финальный текстовый объект
    const textObj = createTextObject({
        text,
        fontSize: clampFontSize(fontSize),
        fill: rgbaColor,
        textAlign: textAlignment,
        fontFamily,
        left: textPosition.left,
        top: textPosition.top,
        textBackgroundColor:
            shouldApplyBackgroundNow && hasBackground && backgroundColor
                ? convertHexWithAlphaToRgba(backgroundColor)
                : null,
        width: innerTextWidth,
    })

    // Пересчитываем размеры после загрузки шрифта
    textObj.initDimensions()

    // Принудительно применяем цвет после initDimensions()
    // initDimensions() может сбросить цвет, поэтому нужно его восстановить
    textObj.set({
        fill: rgbaColor,
    })

    // Для Inter/Montserrat дополнительно пересчитываем размеры через requestAnimationFrame
    // Это гарантирует, что размеры будут пересчитаны после применения шрифта
    // и подложка будет применена с правильными размерами
    if (isKerningSensitiveFont) {
        requestAnimationFrame(() => {
            if (textObj && fabricCanvas.getObjects().includes(textObj)) {
                // Принудительно применяем charSpacing для проблемных шрифтов
                // Это важно, так как charSpacing может быть сброшен при обновлении объекта
                textObj.set({ charSpacing: -15 })
                
                // Пересчитываем размеры после применения шрифта
                textObj.initDimensions()

                // Применяем подложку ПОСЛЕ пересчета размеров
                if (hasBackground && backgroundColor) {
                    const rgbaBackgroundColor =
                        convertHexWithAlphaToRgba(backgroundColor)
                    textObj.set({
                        textBackgroundColor: rgbaBackgroundColor,
                        textBackgroundPadding: TEXT_OBJECT_PADDING.BACKGROUND,
                        textBackgroundBorderRadius:
                            TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
                    })
                }

                // Принудительно применяем цвет после initDimensions()
                textObj.set({ fill: rgbaColor })

                textObj.setCoords()
                fabricCanvas.renderAll()
            }
        })
    } else if (hasBackground && backgroundColor) {
        // Для других шрифтов применяем подложку после initDimensions(), если она не была применена при создании
        // Это нужно на случай, если initDimensions() сбросил подложку
        const rgbaBackgroundColor = convertHexWithAlphaToRgba(backgroundColor)
        textObj.set({
            textBackgroundColor: rgbaBackgroundColor,
            textBackgroundPadding: TEXT_OBJECT_PADDING.BACKGROUND,
            textBackgroundBorderRadius: TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
        })
    }

    // Создаем контрол масштабирования
    const resizeControl = createDefaultResizeControl({
        canvas: fabricCanvas,
        onScaling: () => {
            // Обработчик будет установлен через setupTextObjectEventHandlers
        },
        isScalingFromSlider,
    })

    // Настраиваем контролы текстового объекта
    setupTextObjectControls(textObj, resizeControl)

    // Настраиваем обработчики событий
    setupTextObjectEventHandlers({
        textObj,
        fabricCanvas,
        onScaling: () => {
            // Обработчик будет установлен через хук
        },
        onPositionChange: onPositionChange
            ? (e) => {
                  // Обработчик будет установлен через хук
              }
            : undefined,
    })

    // Применяем настройки обводки
    applyTextBorderStyles(textObj)

    return textObj
}

/**
 * Параметры для обновления текстового объекта
 */
export interface UpdateTextObjectParams {
    textObj: Text
    updates: {
        text?: string
        fontSize?: number
        fill?: string
        textAlign?: TextAlignment
        fontFamily?: FontFamily
        left?: number
        top?: number
        textBackgroundColor?: string | null
    }
}

/**
 * Обновление текстового объекта
 * @param params - параметры для обновления
 */
export function updateTextObject(params: UpdateTextObjectParams): void {
    const { textObj, updates } = params

    const finalUpdates: {
        text?: string
        fontSize?: number
        fill?: string
        textAlign?: string
        fontFamily?: string
        left?: number
        top?: number
        textBackgroundColor?: string | undefined
        textBackgroundPadding?: number | undefined
        textBackgroundBorderRadius?: number | undefined
        originX?: 'left' | 'center' | 'right'
        padding?: number
        lineHeight?: number
        charSpacing?: number
    } = {}

    if (updates.text !== undefined) {
        finalUpdates.text = updates.text
    }

    if (updates.fontSize !== undefined) {
        finalUpdates.fontSize = clampFontSize(updates.fontSize)
    }

    if (updates.fill !== undefined) {
        finalUpdates.fill = convertHexWithAlphaToRgba(updates.fill)
    }

    if (updates.textAlign !== undefined) {
        finalUpdates.textAlign = updates.textAlign
        finalUpdates.originX = updates.textAlign
    }

    // Определяем текущий fontFamily для применения charSpacing
    // Используем переданный fontFamily или текущий из textObj
    const currentFontFamily = updates.fontFamily ?? (textObj.fontFamily as FontFamily | undefined)
    
    if (updates.fontFamily !== undefined) {
        // Нормализуем имя шрифта для Fabric.js
        finalUpdates.fontFamily = getNormalizedFontFamily(updates.fontFamily)
    }

    // Применяем charSpacing для проблемных шрифтов (Inter, Montserrat)
    // Важно: применяем всегда, даже если fontFamily не меняется, чтобы гарантировать правильное значение
    if (currentFontFamily) {
        const currentFont = String(currentFontFamily)
        const isKerningSensitiveFont =
            currentFont === 'Inter' ||
            currentFont === 'Montserrat' ||
            currentFont.includes('Inter') ||
            currentFont.includes('Montserrat')

        const kerningSensitiveCharSpacing = isKerningSensitiveFont ? -15 : 0
        finalUpdates.charSpacing = kerningSensitiveCharSpacing
    }

    if (updates.left !== undefined) {
        // Округляем координату до целых пикселей для четкости текста
        finalUpdates.left = Math.round(updates.left)
    }

    if (updates.top !== undefined) {
        // Округляем координату до целых пикселей для четкости текста
        finalUpdates.top = Math.round(updates.top)
    }

    if (updates.textBackgroundColor !== undefined) {
        if (updates.textBackgroundColor) {
            finalUpdates.textBackgroundColor = convertHexWithAlphaToRgba(
                updates.textBackgroundColor
            )
            finalUpdates.textBackgroundPadding =
                TEXT_OBJECT_PADDING.BACKGROUND
            finalUpdates.textBackgroundBorderRadius =
                TEXT_OBJECT_BACKGROUND_BORDER_RADIUS
        } else {
            finalUpdates.textBackgroundColor = undefined
            finalUpdates.textBackgroundPadding = undefined
            finalUpdates.textBackgroundBorderRadius = undefined
        }
    }

    // Всегда применяем стандартные настройки
    finalUpdates.padding = 10
    finalUpdates.lineHeight = TEXT_OBJECT_LINE_HEIGHT

    textObj.set(finalUpdates)
}

/**
 * Удаление текстового объекта с canvas
 * @param canvas - canvas для удаления объекта
 * @param textObj - текстовый объект для удаления
 */
export function removeTextObject(canvas: Canvas, textObj: Text | null): void {
    if (textObj) {
        canvas.remove(textObj)
    }
}
